import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { GitBranch, Plus, RefreshCw, LogOut, User, Loader, AlertCircle } from 'lucide-react';
import { useAuth } from '../AuthContext';
import FlowChartNode from '../components/FlowChartNode';

const API_URL = 'https://api.triggerdocs.dev/api';

function Repositories() {
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  const [repositories, setRepositories] = useState([]);
  const [selectedRepo, setSelectedRepo] = useState(null);
  const [files, setFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newRepo, setNewRepo] = useState({ name: '', git_url: '', branch: 'main' });
  const [error, setError] = useState(null);

  useEffect(() => {
    loadRepositories();
  }, [token]);

  const loadRepositories = async () => {
    try {
      const response = await fetch(`${API_URL}/repositories`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (!response.ok) throw new Error('Failed to load repositories');
      const data = await response.json();
      setRepositories(data);
    } catch (err) {
      console.error('Error loading repositories:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const addRepository = async (e) => {
    e.preventDefault();
    setError(null);
    try {
      const response = await fetch(`${API_URL}/repositories`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newRepo),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to add repository');
      }

      const repo = await response.json();
      setRepositories([...repositories, repo]);
      setShowAddModal(false);
      setNewRepo({ name: '', git_url: '', branch: 'main' });
      syncRepository(repo.id);
    } catch (err) {
      console.error('Error adding repository:', err);
      setError(err.message);
    }
  };

  const syncRepository = async (repoId) => {
    setIsAnalyzing(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/repositories/${repoId}/sync`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error('Failed to sync repository');
      setTimeout(() => loadRepositoryFiles(repoId), 2000);
    } catch (err) {
      console.error('Error syncing repository:', err);
      setError(err.message);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const loadRepositoryFiles = async (repoId) => {
    try {
      const response = await fetch(`${API_URL}/repositories/${repoId}/files`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error('Failed to load files');
      const data = await response.json();
      setFiles(data);
      setSelectedRepo(repoId);
    } catch (err) {
      console.error('Error loading files:', err);
      setError(err.message);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <Loader className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <div className="border-b border-slate-700/50 bg-slate-900/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <GitBranch className="w-6 h-6 text-blue-500" />
            <h1 className="text-xl font-semibold text-white">Git Repositories</h1>
          </div>
          <div className="flex items-center gap-4">
            {user && (
              <div className="flex items-center gap-2 text-sm text-slate-400">
                <User className="w-4 h-4" />
                <span>{user.email}</span>
              </div>
            )}
            <button onClick={handleLogout} className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-lg transition-colors">
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {error && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/50 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-red-400 font-medium">Error</p>
              <p className="text-red-300 text-sm">{error}</p>
            </div>
          </div>
        )}

        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white">Your Repositories</h2>
            <button onClick={() => setShowAddModal(true)} className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors">
              <Plus className="w-4 h-4" />
              Add Repository
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {repositories.map((repo) => (
              <div key={repo.id} onClick={() => loadRepositoryFiles(repo.id)} className={`p-4 bg-slate-800/50 border rounded-lg cursor-pointer transition-all ${selectedRepo === repo.id ? 'border-blue-500 bg-slate-800' : 'border-slate-700 hover:border-slate-600'}`}>
                <div className="flex items-start justify-between mb-2">
                  <h3 className="font-medium text-white">{repo.name}</h3>
                  <button onClick={(e) => { e.stopPropagation(); syncRepository(repo.id); }} className="p-1 hover:bg-slate-700 rounded transition-colors">
                    <RefreshCw className={`w-4 h-4 text-slate-400 ${isAnalyzing ? 'animate-spin' : ''}`} />
                  </button>
                </div>
                <p className="text-sm text-slate-400 truncate mb-2">{repo.git_url}</p>
                <div className="flex items-center gap-4 text-xs text-slate-500">
                  <span>Branch: {repo.branch}</span>
                  {repo.last_synced_at && <span>Synced: {new Date(repo.last_synced_at).toLocaleDateString()}</span>}
                </div>
              </div>
            ))}
          </div>

          {repositories.length === 0 && (
            <div className="text-center py-12 text-slate-400">
              <GitBranch className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No repositories yet. Add your first repository to get started!</p>
            </div>
          )}
        </div>

        {files.length > 0 && (
          <div className="mt-8">
            <h2 className="text-lg font-semibold text-white mb-4">
              File Analysis
              {isAnalyzing && <span className="ml-2 text-sm text-blue-400">(Analyzing...)</span>}
            </h2>
            <div className="bg-slate-800/30 rounded-lg p-6 border border-slate-700/50">
              <div className="flex flex-wrap gap-4">
                {files.map((file) => (
                  <FlowChartNode key={file.file_path} file={file} onClick={(f) => console.log('File clicked:', f)} />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-lg p-6 w-full max-w-md border border-slate-700">
            <h3 className="text-lg font-semibold text-white mb-4">Add Repository</h3>
            <form onSubmit={addRepository}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Repository Name</label>
                  <input type="text" value={newRepo.name} onChange={(e) => setNewRepo({ ...newRepo, name: e.target.value })} className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-blue-500" placeholder="my-awesome-project" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Git URL</label>
                  <input type="url" value={newRepo.git_url} onChange={(e) => setNewRepo({ ...newRepo, git_url: e.target.value })} className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-blue-500" placeholder="https://github.com/user/repo.git" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Branch</label>
                  <input type="text" value={newRepo.branch} onChange={(e) => setNewRepo({ ...newRepo, branch: e.target.value })} className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-blue-500" placeholder="main" required />
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button type="button" onClick={() => setShowAddModal(false)} className="flex-1 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors">Cancel</button>
                <button type="submit" className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors">Add Repository</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Repositories;
